import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output, inject } from "@angular/core";
import { guid } from "@methods/uniqueMethods";
import { TabMenu } from "@models/generic/TabModels/TabMenu";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";

@Directive({
    selector: "[menuSystem]",
    standalone: true,
})
export class MenuSystemDirective implements AfterViewInit, OnDestroy {
    el = inject(ElementRef);

    @Input() cerrixTab: TabModel;
    @Input() collapsed = false;
    @Input() doNotShowTucked = false;
    @Input() loadLastOnReload = true;
    @Output() changeEvent = new EventEmitter<TabMenuItem>();
    @Output() onInitComplete = new EventEmitter<TabMenuItem>();

    topHtml: HTMLElement;
    activeMenu: string;

    readonly subMenuSelector = "menusystemsubmenu";
    readonly menuItemSelector = "menusystemitem";

    // Translated `name` attributes ([attr.name]="… | transloco") are still empty when
    // the menu is snapshotted; this maps element id -> item so late updates land.
    private menuItemsByElementId = new Map<string, TabMenuItem>();
    private nameObserver: MutationObserver;

    private _tabService = inject(TabService);

    ngAfterViewInit() {
        this.topHtml = this.el.nativeElement as HTMLElement;
        this.topHtml.classList.add(this.cerrixTab.identifier + "-menu-system");

        const menu = new TabMenu();
        menu.menuItemClicked = (menuItem) => {
            this.activateMenuItem(menuItem);
        };

        menu.doNotShowTucked = this.doNotShowTucked;
        menu.menuItems = this.getMenuItems(this.topHtml);
        menu.activeMenuItem = this.activeMenu;

        this.observeNameChanges();

        setTimeout(() => {
            this.cerrixTab.menu = menu;
            this.onInitComplete.emit();
            if (this.loadLastOnReload) {
                this.loadLastActiveTab();
            }
        }, 0);
    }

    getMenuItems(top: HTMLElement): TabMenuItem[] {
        const children = top.children;

        const menuItems: TabMenuItem[] = [];
        for (let i = 0; i < children.length; i++) {
            const child = children[i] as HTMLElement;
            const childType = this.getChildType(child);
            if (childType === MenuType.None) {
                continue;
            }
            if (childType === MenuType.Multiple) {
                throw Error("Child item contains multiple selectors!");
            }

            menuItems.push(this.createMenuItem(child, childType === MenuType.SubMenu));
        }

        return menuItems;
    }

    createMenuItem(element: HTMLElement, isSubMenu: boolean): TabMenuItem {
        const item = new TabMenuItem();

        const elemName = element.getAttribute("name");
        item.name = elemName ? elemName : "Unnamed item";

        const elemIcon = element.getAttribute("menuicon");
        item.iconClass = elemIcon;

        const menuID = this.cerrixTab.identifier + "-" + guid();
        item.identifier = menuID;

        item.menuItemId = element.getAttribute("menuItemId");
        item.menuItemId = item.menuItemId ? item.menuItemId : item.name;

        element.id = menuID;
        this.menuItemsByElementId.set(menuID, item);

        if (isSubMenu) {
            item.clickable = false;
            item.children = this.getMenuItems(element);
        } else {
            item.clickable = true;

            if (!this.activeMenu) {
                this.activeMenu = menuID;
            } else {
                element.classList.add("d-none");
            }
        }

        return item;
    }

    private observeNameChanges() {
        this.nameObserver = new MutationObserver((records) => {
            for (const record of records) {
                const element = record.target as HTMLElement;
                const item = this.menuItemsByElementId.get(element.id);
                if (!item) {
                    continue;
                }

                item.name = element.getAttribute("name") || "Unnamed item";
                if (!element.getAttribute("menuItemId")) {
                    item.menuItemId = item.name;
                }
            }
        });

        this.nameObserver.observe(this.topHtml, {
            subtree: true,
            attributes: true,
            attributeFilter: ["name"],
        });
    }

    ngOnDestroy() {
        if (this.nameObserver) {
            this.nameObserver.disconnect();
        }
    }

    getChildType(element: HTMLElement): MenuType {
        let menuType = MenuType.None;

        const isSubmenu = element.hasAttribute(this.subMenuSelector);
        const isItem = element.hasAttribute(this.menuItemSelector);

        if (isSubmenu && isItem) {
            menuType = MenuType.Multiple;
            return menuType;
        }

        if (isSubmenu) {
            menuType = MenuType.SubMenu;
        }
        if (isItem) {
            menuType = MenuType.MenuItem;
        }
        return menuType;
    }

    activateMenuItem(menuItem: TabMenuItem) {
        if (this.activeMenu) {
            const oldPage = document.getElementById(this.activeMenu);
            oldPage.classList.add("d-none");
        }

        this.activeMenu = menuItem.identifier;
        const newPage = document.getElementById(this.activeMenu);
        newPage.classList.remove("d-none");

        this.cerrixTab.menu.activeMenuItem = this.activeMenu;
        if (this.changeEvent) {
            this.changeEvent.emit(menuItem);
            // Only menu items with a menuItemId are URL-addressable. Action items (e.g. the
            // preset "Create" entry) have no id; syncing them would push ?activemenu=undefined.
            if (this.loadLastOnReload && menuItem.menuItemId) {
                this.storeActiveTab(menuItem);
                this._tabService.activeTabMenuChangedEvent.emit(this.cerrixTab.identifier);
            }
        }
    }

    private loadLastActiveTab() {
        const menuToOpen = this.cerrixTab.config ? this.cerrixTab.config.activemenu : "";
        if (menuToOpen) {
            const menu = this.cerrixTab.menu.getTabMenuItemByMenuItemId(menuToOpen, true);
            if (menu) {
                this.cerrixTab.menu.menuItemClicked(menu);
            }

            return;
        }
    }

    private storeActiveTab(ev: TabMenuItem) {
        if (!this.cerrixTab.config) {
            this.cerrixTab.config = {};
        }

        // Store the raw menu-item id. buildTabPath encodes it once (encodeURIComponent) when
        // it goes to the address bar, and getTabFromUrl / URLSearchParams decode it once on the
        // way back. Pre-encoding here would double-encode (e.g. a space -> %2520).
        this.cerrixTab.config.activemenu = ev.menuItemId;
    }
}

enum MenuType {
    None,
    Multiple,
    SubMenu,
    MenuItem,
}
